/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import Link from '@atlaskit/link';
import { Flex, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { messages } from './messages';
import { WarningIllustration } from './warning-illustration';

const styles = cssMap({
	button: {
		backgroundColor: token('color.background.neutral.subtle'),
		border: 'none',
		display: 'inline',
		color: token('color.link'),
		cursor: 'pointer',
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		marginTop: token('space.0'),
		marginRight: token('space.0'),
		marginBottom: token('space.0'),
		marginLeft: token('space.0'),
	},

	wrapper: {
		height: '100%',
		paddingInline: token('space.400'),
		paddingBlock: token('space.1000'),
	},

	marginTop: {
		marginTop: token('space.300'),
	},
});

export type ChatGenericErrorType = 'generic' | 'rate-limited' | 'rovo-inactive';

type ChatGenericErrorProps = {
	errorType: ChatGenericErrorType;
};

export const ChatGenericError = ({ errorType }: ChatGenericErrorProps): JSX.Element => {
	const { formatMessage } = useIntl();

	const getTitle = () => {
		switch (errorType) {
			case 'rate-limited':
				return messages.rateLimitedTitle;
			case 'rovo-inactive':
				return messages.rovoInactiveTitle;
			default:
				return messages.title;
		}
	};

	const getDescriptionRender = () => {
		switch (errorType) {
			case 'rate-limited':
				return formatMessage(messages.rateLimitedDescription);
			case 'rovo-inactive':
				return formatMessage(messages.rovoInactiveDescription, {
					link: (chunks: ReactNode[]) => (
						<Link href="https://www.atlassian.com/software/rovo" target="_blank">
							{chunks}
						</Link>
					),
				});
			default:
				return (
					<Pressable
						xcss={styles.button}
						onClick={() => {
							window.location.reload();
						}}
					>
						{formatMessage(messages.reloadLink)}
					</Pressable>
				);
		}
	};

	return (
		<Flex
			columnGap="space.100"
			alignItems="center"
			justifyContent="center"
			direction="column"
			xcss={styles.wrapper}
			testId="ai-mate-generic-error"
		>
			<WarningIllustration />
			<Stack xcss={styles.marginTop} space="space.150" alignInline="center">
				<Heading size="medium">{formatMessage(getTitle())}</Heading>
				<Text as="p">{getDescriptionRender()}</Text>
			</Stack>
		</Flex>
	);
};
